import { useEffect } from 'react';
import seo from './pages.json';

export interface HeadTags {
  title: string;
  description?: string;
  /** Path on the apex domain, e.g. "/notes". Omit for noindex pages. */
  canonicalPath?: string;
  noindex?: boolean;
  image?: string;
}

type PageSeo = { title: string; description: string; h1: string; intro: string };

export const SITE_URL = seo.siteUrl;
export const PAGES = seo.pages as Record<string, PageSeo>;
export const NOT_FOUND = seo.notFound as PageSeo;

const absolute = (path: string) => (/^https?:\/\//.test(path) ? path : SITE_URL + path);

function setMeta(attr: 'name' | 'property', key: string, content: string | null) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href: string | null) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Updates the tags that the build-time prerender (client/scripts/prerender-routes.js)
 * writes into each route's static HTML, so client-side navigation stays in sync.
 */
export function applyHead({ title, description, canonicalPath, noindex, image }: HeadTags) {
  document.title = title;
  const desc = description ?? null;
  const url = canonicalPath && !noindex ? absolute(canonicalPath) : null;
  const img = absolute(image ?? seo.defaultImage);

  setMeta('name', 'description', desc);
  setMeta('name', 'robots', noindex ? 'noindex' : null);
  setCanonical(url);

  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', desc);
  setMeta('property', 'og:url', url);
  setMeta('property', 'og:image', img);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', desc);
  setMeta('name', 'twitter:image', img);
}

// Public pages whose content comes from the API. They stay indexable; the page
// component refines the title/description once its data loads.
const PUBLIC_DYNAMIC = /^\/(stories|news)\/[^/]+$/;

// Individual notes (/notes/:publicId) are user-submitted and can be edited or
// unpublished by moderators at any time, so they are never indexed. The /notes
// list page is the indexable entry point for them.
export const NOTE_DETAIL_TITLE = 'A Note of Hope | Stamp Out Suicide';
export const NOTE_DETAIL_DESCRIPTION =
  'An uplifting note written to encourage someone who is struggling. In crisis? Call or text 988.';
export const NOTE_NOT_FOUND_TITLE = 'Note not found | Stamp Out Suicide';

/**
 * Head tags for a path: its static SEO copy, a generic indexable default for
 * public story/news detail pages, or noindex for everything else (note detail,
 * auth, dashboards, admin).
 */
export function headForPath(pathname: string): HeadTags {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const page = PAGES[path];
  if (page) {
    return { title: page.title, description: page.description, canonicalPath: path };
  }
  if (PUBLIC_DYNAMIC.test(path)) {
    return { title: seo.defaultTitle, description: PAGES['/'].description, canonicalPath: path };
  }
  return { title: seo.defaultTitle, noindex: true };
}

/** Sets head tags for pages whose metadata depends on loaded data. */
export function useDocumentHead(head: HeadTags | null) {
  const key = head ? JSON.stringify(head) : '';
  useEffect(() => {
    if (head) applyHead(head);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
